import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { login, getToken, formatApiErrorDetail } from "./adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck } from "lucide-react";
import { LOGO_URL } from "../constants/site";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-bg flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Sunbright" className="h-12 w-auto" />
          <div>
            <div className="font-display text-lg font-semibold leading-tight">Sunbright</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">CMS · Admin</div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-medium tracking-tight">Welcome back</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in to manage site content and leads.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <Input
              data-testid="admin-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 h-11"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Password</label>
            <Input
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-11"
              required
            />
          </div>
          {err && (
            <div data-testid="admin-login-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full h-11 bg-primary text-white hover:bg-[#059669]"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
          Sessions expire after 7 days.
        </div>

        <a href="/" className="mt-8 block text-xs text-muted-foreground hover:text-primary">← Back to website</a>
      </div>
    </div>
  );
}
